import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, Field } from 'react-final-form';
import { Link as RouterLink } from 'react-router-dom';
import { Unstable_Grid2 as Grid } from '@mui/material';
import DatePickerFieldAdapter from 'components/shared/field-adapters/DatePickerFieldAdapter';
import TypeaheadFieldAdapter from 'components/shared/field-adapters/TypeaheadFieldAdapter';
import DynamicExerciseFields from 'components/performance/DynamicExerciseFields';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import routes from 'common/routes';
import requestStatus from 'common/constants/status';
import { isRequiredField } from 'common/utils/general';
import { handleClientIdChange } from 'common/utils/user';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import AppBarContext from 'context/AppBarContext';
import performanceSlice from 'store/modules/performance';
import useForms from 'hooks/useForms';

const requiredFields = ['user_id', 'start_date', 'end_date'];

const createInitialValues = {
  user_id: null,
  start_date: null,
  end_date: null
};

// Format JS Date to YYYY-MM-DD for GraphQL Date type
const formatDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function PerformanceCreateDynamic() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'routes', 'labels']);
  const pageTitle = t('performance.create', { ns: 'routes' });
  const formRef = useRef(null);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const { clearClientSearchResults } = useForms();

  const clientOptions = useSelector((state) => state.client.data.search);
  const searchStatus = useSelector((state) => state.client.status.search);
  const status = useSelector((state) => state.performance.status.createDynamic);

  const [exercisesData, setExercisesData] = useState([]);
  const [exerciseKey, setExerciseKey] = useState(0);

  useEffect(() => {
    if (status === requestStatus.success) {
      formRef.current.restart();
      setExercisesData([]);
      sessionStorage.removeItem('otrix_draft_exercises');
      setExerciseKey((prev) => prev + 1);
    }
  }, [status]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  // Include membership_status in client search to validate active clients
  const clientSearchFields = `
    id
    full_name
    membership_status
  `;
  const onClientIdChange = handleClientIdChange(dispatch, clientSearchFields);

  const handleExercisesChange = useCallback((exercises) => {
    setExercisesData(exercises);
  }, []);

  const onCreate = useCallback(
    (values) => {
      const clientId = values.user_id?.value || values.user_id;
      const periodStartDate = formatDate(values.start_date);
      const periodEndDate = formatDate(values.end_date);

      // Build one input per exercise card
      const exercises = exercisesData
        .filter((ex) => ex.exercise_id && ex.sets && ex.sets.length > 0)
        .map((ex) => ({
          client_id: String(clientId),
          exercise_id: String(ex.exercise_id),
          period_start_date: periodStartDate,
          period_end_date: periodEndDate,
          observations: ex.observations || null,
          sets: ex.sets.map((set, index) => ({
            set_number: index + 1,
            time_seconds: set.time_seconds ? parseInt(set.time_seconds, 10) : null,
            repetitions: set.repetitions ? parseInt(set.repetitions, 10) : null,
            weight_kg: set.weight_kg ? parseFloat(set.weight_kg) : null,
            distance_km: set.distance_km ? parseFloat(set.distance_km) : null
          }))
        }));

      dispatch(performanceSlice.actions.createDynamicBatch({ exercises }));
    },
    [dispatch, exercisesData]
  );

  const validate = (values) => {
    const errors = {};
    if (!values.user_id) {
      errors.user_id = t('required', { ns: 'validations' });
    }
    if (!values.start_date) {
      errors.start_date = t('required', { ns: 'validations' });
    }
    if (!values.end_date) {
      errors.end_date = t('required', { ns: 'validations' });
    }
    return errors;
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={createInitialValues}
        onSubmit={onCreate}
        validate={validate}
        render={({ handleSubmit, form: formApi, invalid, values }) => {
          formRef.current = formApi;

          // Check if client is active and has exercises
          const selectedClient = values.user_id;
          const isClientActive = selectedClient?.membership_status === 'ACTIVE';
          const hasExercises = exercisesData.length > 0;
          const canSubmit = isClientActive && hasExercises;

          return (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={4}>
                <Grid xs={12}>
                  <Field
                    id="user_id"
                    name="user_id"
                    component={TypeaheadFieldAdapter}
                    fullWidth
                    loading={searchStatus === requestStatus.loading}
                    isMultiple={false}
                    onClose={clearClientSearchResults}
                    onInputChange={onClientIdChange}
                    options={clientOptions}
                    renderInputProps={{
                      label: t('client', { count: 1, ns: 'labels' }),
                      required: isRequiredField('user_id', requiredFields)
                    }}
                  />
                </Grid>
                <Grid xs={12} sm={6}>
                  <Field
                    id="start_date"
                    name="start_date"
                    component={DatePickerFieldAdapter}
                    label={t('startDate', { ns: 'labels' })}
                    renderInputProps={{
                      fullWidth: true,
                      required: isRequiredField('start_date', requiredFields)
                    }}
                  />
                </Grid>
                <Grid xs={12} sm={6}>
                  <Field
                    id="end_date"
                    name="end_date"
                    component={DatePickerFieldAdapter}
                    label={t('endDate', { ns: 'labels' })}
                    renderInputProps={{
                      fullWidth: true,
                      required: isRequiredField('end_date', requiredFields)
                    }}
                  />
                </Grid>
              </Grid>

              {/* Dynamic Exercises Section */}
              <DynamicExerciseFields
                key={exerciseKey}
                onExercisesChange={handleExercisesChange}
                selectedClient={selectedClient}
              />

              <StyledButtonsContainer>
                <SecondaryButtonLoading
                  component={RouterLink}
                  disabled={status === requestStatus.loading}
                  style={{ marginRight: '32px' }}
                  label={t('cancel', { ns: 'buttons' })}
                  onClick={() => sessionStorage.removeItem('otrix_draft_exercises')}
                  to={routes.listPerformance.path}
                  type="button"
                />
                <PrimaryLoadingButton
                  disabled={invalid || !canSubmit || status === requestStatus.loading}
                  loading={status === requestStatus.loading}
                  label={t('performance.create', { ns: 'buttons' })}
                  type="submit"
                />
              </StyledButtonsContainer>
            </form>
          );
        }}
      />
    </>
  );
}

export default PerformanceCreateDynamic;
